import Decimal from 'decimal.js';
import calculateTwoLegSpreadQuote from './calculateTwoLegSpreadQuote';
import roundMid from './roundMid';
import { BidAskMidFn } from '../types/BidAskMidFn';
import { Strategy } from '@oai-frontend-monorepo/types';
import SymbolParser from '../../symbol/SymbolParser';
import getLegsFromTrade from '../getLegsFromTrade';
import decimal from '../../../math/decimal';
import getSpreadWidth from '../getSpreadWidth';

const calculateFourLegSpreadQuote: BidAskMidFn = ({
  legs,
  strategy,
  isSell,
}) => {
  if (!legs?.length || !strategy) {
    return undefined;
  }

  const { shortCall, longCall, longPut, shortPut } = getLegsFromTrade({
    optionContracts: legs,
    strategy: strategy,
  });

  if (!shortCall || !longCall || !longPut || !shortPut) {
    return undefined;
  }

  const callSpreadQuote = calculateTwoLegSpreadQuote({
    legs: [shortCall!.optionContract, longCall!.optionContract],
    strategy: Strategy.SHORT_CALL_SPREAD,
  });

  const putSpreadQuote = calculateTwoLegSpreadQuote({
    legs: [shortPut!.optionContract, longPut!.optionContract],
    strategy: Strategy.SHORT_PUT_SPREAD,
  });

  if (
    callSpreadQuote?.ask === undefined ||
    callSpreadQuote.mid === undefined ||
    callSpreadQuote.bid === undefined ||
    putSpreadQuote?.mid === undefined ||
    putSpreadQuote.ask === undefined ||
    putSpreadQuote.bid === undefined
  ) {
    return undefined;
  }

  const shortCallContract = shortCall.optionContract;
  const longCallContract = longCall.optionContract;
  const shortPutContract = shortPut.optionContract;
  const longPutContract = longPut.optionContract;

  const parser = new SymbolParser(legs[0].symbol);
  const isSPX = parser.isSPX;
  const ROUNDING_VALUE = isSPX ? 0.05 : 0.01;

  const maxWidth = new Decimal(
    getSpreadWidth({
      strategy,
      underlyingSymbol: parser.underlyingSymbol as string,
      symbols: legs.map((l) => l.symbol),
    }) ?? 0
  );

  const ask = new Decimal(shortCallContract.ask)
    .minus(longCallContract.bid)
    .plus(shortPutContract.ask)
    .minus(longPutContract.bid)
    .toNumber();

  const bid = new Decimal(shortCallContract.bid)
    .minus(longCallContract.ask)
    .plus(shortPutContract.bid)
    .minus(longPutContract.ask)
    .toNumber();

  const mid = new Decimal(ask).plus(bid).dividedBy(2);

  const valueToUse = mid.greaterThan(maxWidth) ? maxWidth : mid;

  return {
    ask,
    mid:
      roundMid({
        mid: valueToUse,
        strategy,
        isSell,
      })
        ?.toNearest(ROUNDING_VALUE)
        .toNumber() || 0.01,
    bid: bid,
    open:
      roundMid({
        mid: decimal(callSpreadQuote.open)?._plus?.(putSpreadQuote.open),
        strategy,
        isSell,
      })
        ?.toNearest(ROUNDING_VALUE)
        .toNumber() || 0.01,
    last:
      roundMid({
        mid: decimal(callSpreadQuote.last)?._plus?.(putSpreadQuote.last),
        strategy,
        isSell,
      })
        ?.toNearest(ROUNDING_VALUE)
        .toNumber() || 0.01,
    close:
      roundMid({
        mid: decimal(callSpreadQuote.close)?._plus?.(putSpreadQuote.close),
        strategy,
        isSell,
      })
        ?.toNearest(ROUNDING_VALUE)
        .toNumber() || 0.01,
    prevclose:
      roundMid({
        mid: decimal(callSpreadQuote.prevclose)?._plus?.(
          putSpreadQuote.prevclose
        ),
        strategy,
        isSell,
      })
        ?.toNearest(ROUNDING_VALUE)
        .toNumber() || 0.01,
  };
};

export default calculateFourLegSpreadQuote;
